import { HIGH_PERCENTAGE_BREAKPOINT } from '@/constants/chart.constants';
import {
  Insight,
  InsightLanguageDistribution,
  TimeRange,
} from '@/services/dashboard-service/dashboard.service.types';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { BusinessHourItem } from '@/services/business-hours/business-hours.types';

dayjs.extend(utc);

export const calculatePercentage = (value: number | null, size: number) => {
  if (!value || size <= 0) return 0;

  const percent = (value / size) * 100;
  return percent < 1 ? Number(percent.toFixed(1)) : Math.floor(percent);
};

export const calculatePercentageDecimal = (value: number | null, size: number) =>
  value && size > 0 ? Number(((value / size) * 100).toFixed(2)) : 0;

export const sumDataValue = (data: Insight) => {
  const mapNullsToZero = Object.values(data).map((value) => (value ? value : 0));
  return mapNullsToZero.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
};

export const sumLanguageDistributionData = (data: InsightLanguageDistribution) => {
  const counts = Object.values(data).map((item) => item.count || 0);
  return counts.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
};

export const isValueHigherThanBreakpoint = (value: number) => value >= HIGH_PERCENTAGE_BREAKPOINT;

export const calculateSecondsToMinutes = (duration: number) => {
  const time = new Date(duration * 1000);
  const hours = time.getUTCHours();
  const minutes = hours ? hours * 60 + time.getUTCMinutes() : time.getUTCMinutes();

  return `${String(minutes).padStart(2, '0')}:${String(time.getSeconds()).padStart(2, '0')}`;
};

export const getSortedInsight = (
  data: Insight | undefined,
  count?: number
): Insight | undefined => {
  if (!data) {
    return data;
  }
  const dataArray = Object.entries(data).map(([key, value]) => ({ name: key, value }));
  const sorted = dataArray.sort(
    ({ value: valueA }, { value: valueB }) => (valueB || 0) - (valueA || 0)
  );

  const sliced = sorted.slice(0, count);

  return sliced.reduce((acc, curr) => ({ ...acc, [curr.name]: curr.value }), {});
};

export const calculateDateFromISOtoYYYY_MM_DD = (date: string) => {
  return new Date(date).toISOString().substring(0, 10);
};

export const calculateHourFromISODate = (date: string) => {
  return new Date(date).toLocaleString('en-us', { hour: 'numeric', minute: 'numeric' });
};

export const downloadCSVFile = (data: string) => {
  const csvBlob = new Blob([data], { type: 'text/csv' });
  const aElement = document.createElement('a');
  aElement.setAttribute(
    'download',
    `calls-export_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}.csv`
  );
  const href = URL.createObjectURL(csvBlob);
  aElement.href = href;
  aElement.setAttribute('target', '_blank');
  aElement.click();
  URL.revokeObjectURL(href);
};

export const downloadTrendingTopicsCSVFile = (data: string) => {
  const csvBlob = new Blob([data], { type: 'text/csv' });
  const aElement = document.createElement('a');
  aElement.setAttribute(
    'download',
    `trending-topics_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}.csv`
  );
  const href = URL.createObjectURL(csvBlob);
  aElement.href = href;
  aElement.setAttribute('target', '_blank');
  aElement.click();
  URL.revokeObjectURL(href);
};

export const downloadAudioFile = (audioData: Blob, fileName: string = 'audio-file') => {
  const audioBlob = new Blob([audioData], { type: 'audio/mpeg' });
  const aElement = document.createElement('a');
  aElement.setAttribute(
    'download',
    `${fileName}_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}.mp3`
  );
  const href = URL.createObjectURL(audioBlob);
  aElement.href = href;
  aElement.setAttribute('target', '_blank');
  aElement.click();
  URL.revokeObjectURL(href);
};

export const anonymizeEmail = (email: string) => {
  const emailArray = email.split('@');
  const halfOfTheFirstWord = Math.floor(emailArray[0].length / 2);

  emailArray[0] = emailArray[0]
    .split('')
    .map((char, index) => {
      if (index >= halfOfTheFirstWord) {
        return char;
      }
      return '*';
    })
    .join('');

  return emailArray.join('@');
};

export const anonymizePhone = (phone: string) => {
  const cleanPhone = phone.replace(/\D/g, '');
  if (cleanPhone.length <= 4) return '*'.repeat(cleanPhone.length);

  const start = cleanPhone.slice(0, 3);
  const end = cleanPhone.slice(-2);
  const hidden = '*'.repeat(cleanPhone.length - 5);

  return `${start}${hidden}${end}`;
};

export function convertToSeconds(duration: string): number {
  const [minutes, seconds] = duration.split(':').map(Number);
  const totalSeconds = minutes * 60 + seconds;
  return totalSeconds;
}

export const countSeconds = (setTime: (value: number) => void, startTime: number) => {
  let totalSeconds = startTime;

  const intervalId = setInterval(() => {
    totalSeconds--;
    setTime(totalSeconds);

    if (totalSeconds <= 0) {
      clearInterval(intervalId);
    }
  }, 1000);

  return () => clearInterval(intervalId);
};

export const getRangeDate = (selectedFilter: TimeRange, startTime?: string, endTime?: string) => {
  if (startTime && endTime) {
    return {
      currentDate: calculateDateFromISOtoYYYY_MM_DD(endTime),
      newDate: calculateDateFromISOtoYYYY_MM_DD(startTime),
    };
  }

  const days = Number(selectedFilter.replace(/\D/g, ''));
  const currentDate = new Date();
  const newDate = new Date(currentDate);
  newDate.setDate(currentDate.getDate() - days);

  return {
    currentDate: calculateDateFromISOtoYYYY_MM_DD(currentDate.toISOString()),
    newDate: calculateDateFromISOtoYYYY_MM_DD(newDate.toISOString()),
  };
};

export const formatToHTML = (text: string) => {
  return text
    .replace(/\n\n/g, '<br><br>')
    .replace(/\n-/g, '<br>-')
    .replace(/\n/g, '<br>')
    .replace(/^- (.*?)(?=\n|$)/gm, '<li>$1</li>')
    .replace(/<br><li>/g, '<ul><li>')
    .replace(/<\/li>(?!<li>)/g, '</li></ul>');
};

export function capitalizeFirstLetter(str: string) {
  return str.replace(/^./, (char) => char.toUpperCase());
}

export const calculateBarchartWidth = (numberOfItems = 9) => {
  if (numberOfItems > 9) return numberOfItems * 34;
  return '100%';
};

export const getHoursForDate = (
  temporary: BusinessHourItem[],
  permanent: BusinessHourItem[],
  date: Dayjs | null
): BusinessHourItem[] => {
  if (!date) return [];

  const dateStr = date.format('YYYY-MM-DD');

  const tempHours = temporary.filter((item) => {
    if (!item.start_time) return false;
    // Parse ISO string with timezone info (not as UTC)
    const itemDate = dayjs(item.start_time).format('YYYY-MM-DD');
    return itemDate === dateStr;
  });

  if (tempHours.length > 0) return tempHours;

  const weekday = date.format('dddd').toLowerCase();
  return permanent.filter((item) => item.wday === weekday);
};

export const formatTimeRange = (
  startTime: string | null,
  endTime: string | null
): string | null => {
  if (!startTime || !endTime) return null;
  // Extract time portion from ISO string (e.g., "2025-08-27T08:00:00.000-06:00" -> "08:00")
  const extractTime = (isoString: string) => {
    const match = isoString.match(/T(\d{2}:\d{2})/);
    return match ? match[1] : '';
  };
  return `${extractTime(startTime)} – ${extractTime(endTime)}`;
};
